/**
 * External Browser Utility for WebView (Web2Native) App Store Conformity
 * 
 * All payment/checkout processes must happen in an external browser (Safari/Chrome),
 * NOT inside the app's WebView, to comply with App Store guidelines.
 */

// Payment domains that must always be opened externally
export const PAYMENT_DOMAINS = [
  'checkout.stripe.com',
  'billing.stripe.com',
  'connect.stripe.com',
  'pay.stripe.com',
  'invoice.stripe.com',
  'buy.stripe.com',
  'paypal.com',
  'www.paypal.com',
  'klarna.com',
  'www.klarna.com',
  'pay.google.com',
  'apple.com/apple-pay',
  'shop.musikonzept.de', // Shop subdomain (stays on .de)
];

// Internal checkout routes that should trigger external browser in WebView
export const CHECKOUT_ROUTES = [
  '/checkout/',
  '/themenreise-checkout/',

  '/abo-checkout/',
  '/pay/',
  '/subscribe/',
  '/order/',
  '/payment/',
  '/billing/',
  '/invoice/',
  '/upgrade/',
  '/preise',
  '/abo-',
];

// Keywords that indicate payment-related content
export const PAYMENT_KEYWORDS = [
  'checkout',
  'payment',
  'billing',
  'preise',
  'buchen',
  'zahlung',
  'kaufen',
  'upgrade',
  'shop',
];

/**
 * Comprehensive check if a URL should be opened externally
 * Combines domain, route, and keyword checks
 */
export const shouldOpenExternally = (url: string): boolean => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  
  return (
    isPaymentUrl(url) ||
    isCheckoutRoute(url) ||
    PAYMENT_KEYWORDS.some(keyword => lowerUrl.includes(keyword))
  );
};

/**
 * Checks if a URL is a payment-related URL (external payment provider)
 */
export const isPaymentUrl = (url: string): boolean => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  return PAYMENT_DOMAINS.some(domain => lowerUrl.includes(domain.toLowerCase()));
};

/**
 * Checks if a path is a checkout route
 */
export const isCheckoutRoute = (path: string): boolean => {
  if (!path) return false;
  const lowerPath = path.toLowerCase();
  return CHECKOUT_ROUTES.some(route => lowerPath.includes(route.toLowerCase()));
};

/**
 * Detects if the app is running inside a WebView
 * This helps identify when we need to force external browser behavior
 */
export const isWebView = (): boolean => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  
  // iOS WebView detection
  const isIOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent);
  
  // Android WebView detection
  const isAndroidWebView = /wv|WebView/i.test(userAgent) && /Android/i.test(userAgent);
  
  // Web2Native / custom WebView indicators
  const hasWebViewIndicator = /Web2Native|MusiKonzept/i.test(userAgent);
  
  return isIOSWebView || isAndroidWebView || hasWebViewIndicator;
};

/**
 * Opens a URL in an external browser
 * This is the primary function to use for all payment/checkout redirects
 * 
 * For WebViews: Uses target="_blank" + rel="noopener noreferrer" which
 * Web2Native can intercept to open in the system browser
 * 
 * For regular browsers: Opens in a new tab
 */
export const openExternalBrowser = (url: string): void => {
  if (!url) {
    console.error('[ExternalBrowser] No URL provided');
    return;
  }

  console.log('[ExternalBrowser] Opening URL externally:', url);
  
  // Method 1: Try window.open first – some WebView bridges intercept this.
  // Ohne „noopener“ in den Eigenschaften: Damit liefert window.open laut Standard immer null, auch wenn der Tab
  // aufgeht, und der Ersatzweg unten öffnete einen zweiten Tab (Testplan r2-kursseite, 19.10.2026). Die Verbindung
  // zum neuen Tab wird stattdessen hier gekappt.
  const win = window.open(url, '_blank');
  if (win) {
    try {
      win.opener = null;
    } catch {
      // fremder Ursprung, nichts zu tun
    }
    return;
  }

  // Method 2: If window.open was blocked or returned null, fall back to anchor element
  console.log('[ExternalBrowser] window.open blocked, trying anchor fallback');
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Handles a checkout redirect - ensures payment URLs go to external browser
 * Use this instead of window.location.href for any payment redirects
 */
export const handleCheckoutRedirect = (url: string): void => {
  if (!url) {
    console.error('[ExternalBrowser] No checkout URL provided');
    return;
  }

  // Always open payment/checkout URLs externally
  if (isPaymentUrl(url) || isCheckoutRoute(url)) {
    console.log('[ExternalBrowser] Payment URL detected, forcing external browser');
    openExternalBrowser(url);
    return;
  }

  // For non-payment URLs, still use external browser for safety in WebView context
  if (isWebView()) {
    console.log('[ExternalBrowser] WebView detected, opening externally');
    openExternalBrowser(url);
    return;
  }

  // Regular browser: use standard redirect
  console.log('[ExternalBrowser] Standard browser, using window.open');
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Opens the Stripe Customer Portal in an external browser
 */
export const openCustomerPortal = (url: string): void => {
  console.log('[ExternalBrowser] Opening Customer Portal externally');
  openExternalBrowser(url);
};

/**
 * Smart navigation for checkout/upgrade routes
 * In WebView: Opens URL in external browser (Safari)
 * In regular browser: Uses normal SPA navigation
 * 
 * Use this instead of navigate() for any payment/checkout/upgrade flows
 */
export const navigateToCheckout = (path: string, navigate: (path: string) => void): void => {
  const fullUrl = `${window.location.origin}${path}`;
  
  if (isWebView() || shouldOpenExternally(fullUrl)) {
    console.log('[ExternalBrowser] Checkout navigation, opening externally:', fullUrl);
    openExternalBrowser(fullUrl);
  } else {
    navigate(path);
  }
};
